import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/asArray";
import {
  CheckCircle,
  Shield,
  Lock,
  FileText,
  Download,
  Loader2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Upload,
  Link as LinkIcon,
  Clock,
  Eye,
  Plus,
  Trash2,
} from "lucide-react";
import { ComplianceChecklistItem, ComplianceTemplate, ComplianceEvidence } from "@shared/schema";

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  missing: { label: "Missing", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" },
  review_needed: { label: "Review Needed", color: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" },
  action_needed: { label: "Action Needed", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
};

const AREA_LABELS: Record<string, { label: string; icon: React.ReactNode }> = {
  cobra: { label: "COBRA", icon: <Shield className="h-4 w-4" /> },
  hipaa: { label: "HIPAA", icon: <Lock className="h-4 w-4" /> },
  erisa: { label: "ERISA", icon: <FileText className="h-4 w-4" /> },
  aca: { label: "ACA", icon: <CheckCircle className="h-4 w-4" /> },
  fmla: { label: "FMLA", icon: <Clock className="h-4 w-4" /> },
};

export function QuickTemplates({ areaKey }: { areaKey?: string }) {
  const { data: templatesData } = useQuery<ComplianceTemplate[]>({
    queryKey: ['/api/compliance/templates', areaKey],
    queryFn: async () => {
      const url = areaKey ? `/api/compliance/templates?areaKey=${areaKey}` : '/api/compliance/templates';
      const res = await fetch(url, { credentials: 'include' });
      return res.json();
    }
  });
  // Tier-gated 403 body is an object, not a list
  const templates = asArray<ComplianceTemplate>(templatesData);

  if (templates.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
        <ExternalLink className="h-3 w-3" />
        Quick Templates & Resources
      </h4>
      <div className="flex flex-wrap gap-2">
        {templates.map((template) => (
          <a
            key={template.id}
            href={template.linkUrl || template.fileUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 rounded-full transition-colors"
          >
            {template.type === 'link' ? <LinkIcon className="h-3 w-3" /> : <Download className="h-3 w-3" />}
            {template.title}
          </a>
        ))}
      </div>
    </div>
  );
}

const TPA_OPTIONS = [
  { value: 'self', label: 'Self Administered' },
  { value: 'wex', label: 'WEX Health' },
  { value: 'benefitfocus', label: 'Benefitfocus' },
  { value: 'businessolver', label: 'Businessolver' },
  { value: 'conexis', label: 'CONEXIS' },
  { value: 'ebpa', label: 'EBPA' },
  { value: 'tasc', label: 'TASC' },
  { value: 'other', label: 'Other TPA' },
];

function ChecklistItemRow({ 
  item, 
  onStatusChange,
  clientId 
}: { 
  item: ComplianceChecklistItem; 
  onStatusChange: (id: number, status: string) => void;
  clientId: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [evidenceToDelete, setEvidenceToDelete] = useState<number | null>(null);
  const [selectedTpa, setSelectedTpa] = useState<string>('');
  const [evidenceForm, setEvidenceForm] = useState({
    type: 'link' as 'file' | 'link',
    linkUrl: '',
    visibility: 'internal' as 'internal' | 'employee',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const statusInfo = STATUS_LABELS[item.status] || STATUS_LABELS.missing;
  
  const { data: evidenceData, refetch: refetchEvidence } = useQuery<ComplianceEvidence[]>({
    queryKey: ['/api/compliance/evidence', clientId, item.id],
    queryFn: async () => {
      const res = await fetch(`/api/compliance/evidence?clientId=${clientId}&checklistItemId=${item.id}`, { credentials: 'include' });
      return res.json();
    },
    enabled: expanded
  });
  const evidence = asArray<ComplianceEvidence>(evidenceData);

  const addEvidenceMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/compliance/evidence', {
        clientId,
        checklistItemId: item.id,
        type: evidenceForm.type,
        linkUrl: evidenceForm.type === 'link' ? evidenceForm.linkUrl : null,
        visibility: evidenceForm.visibility,
      });
    },
    onSuccess: () => {
      refetchEvidence();
      setShowAddEvidence(false);
      setEvidenceForm({ type: 'link', linkUrl: '', visibility: 'internal' });
      toast({ title: "Evidence added", description: "Evidence has been attached to this checklist item." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to add evidence.", variant: "destructive" });
    }
  });

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast({ title: "Error", description: "Please select a file.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('clientId', String(clientId));
      formData.append('checklistItemId', String(item.id));
      formData.append('visibility', evidenceForm.visibility);

      const res = await fetch('/api/compliance/evidence/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!res.ok) throw new Error('Upload failed');

      refetchEvidence();
      setShowAddEvidence(false);
      setSelectedFile(null);
      setEvidenceForm({ type: 'link', linkUrl: '', visibility: 'internal' });
      toast({ title: "File uploaded", description: "File has been attached as evidence." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to upload file.", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const deleteEvidenceMutation = useMutation({
    mutationFn: async (evidenceId: number) => {
      return apiRequest('DELETE', `/api/compliance/evidence/${evidenceId}`, {});
    },
    onSuccess: () => {
      refetchEvidence();
      toast({ title: "Evidence removed", description: "Evidence has been removed." });
    }
  });

  const handleToggleComplete = () => {
    const newStatus = item.status === 'completed' ? 'missing' : 'completed';
    onStatusChange(item.id, newStatus);
  };

  const handleAddEvidence = () => {
    if (evidenceForm.type === 'link' && !evidenceForm.linkUrl) {
      toast({ title: "Error", description: "Please enter a URL.", variant: "destructive" });
      return;
    }
    addEvidenceMutation.mutate();
  };

  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <div 
          className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <Checkbox 
            checked={item.status === 'completed'} 
            onCheckedChange={handleToggleComplete}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {item.title}
              </span>
              <Badge 
                variant="outline" 
                className={`text-[10px] px-1.5 py-0 ${
                  item.isRequired === false 
                    ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800' 
                    : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                }`}
              >
                {item.isRequired === false ? 'Suggested' : 'Required'}
              </Badge>
              {item.formType && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {item.formType.replace(/_/g, ' ').toUpperCase()}
                </Badge>
              )}
              {item.reviewFrequency === 'annual' && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800">
                  Annual Review
                </Badge>
              )}
            </div>
            {item.description && (
              <p className="text-xs text-muted-foreground truncate">{item.description}</p>
            )}
          </div>
          <Badge className={`${statusInfo.color} text-[10px] px-2`}>{statusInfo.label}</Badge>
          {item.dueDate && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(item.dueDate).toLocaleDateString()}
            </span>
          )}
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
        
        {expanded && (
          <div className="px-3 pb-3 pt-0 border-t bg-muted/30">
            <div className="space-y-3 pt-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground">Status:</span>
                {Object.entries(STATUS_LABELS).map(([key, { label, color }]) => (
                  <button
                    key={key}
                    onClick={() => onStatusChange(item.id, key)}
                    className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                      item.status === key ? color + ' ring-2 ring-offset-1 ring-primary' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              
              {item.reviewFrequency === 'annual' && (
                <div className="text-xs bg-purple-50 dark:bg-purple-900/20 p-2 rounded border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <Clock className="h-3 w-3" />
                    <span className="font-medium">Annual Review Required</span>
                  </div>
                  <div className="mt-1 text-purple-600 dark:text-purple-400 space-y-0.5">
                    {item.nextResetDate && (
                      <p>Next review due: {new Date(item.nextResetDate).toLocaleDateString()}</p>
                    )}
                    {item.lastResetDate && (
                      <p>Last reset: {new Date(item.lastResetDate).toLocaleDateString()}</p>
                    )}
                    <p className="text-[10px] opacity-75">This item will automatically reset to "Missing" on its annual review date.</p>
                  </div>
                </div>
              )}

              {item.notes && (
                <div className="text-xs text-muted-foreground bg-background p-2 rounded">
                  <strong>Notes:</strong> {item.notes}
                </div>
              )}
              
              {item.title.toLowerCase().includes('cobra administrator') && (
                <div className="space-y-2 p-3 bg-background rounded border">
                  <Label className="text-xs font-medium">COBRA Administrator</Label>
                  <Select value={selectedTpa} onValueChange={setSelectedTpa}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select administrator..." />
                    </SelectTrigger>
                    <SelectContent>
                      {TPA_OPTIONS.map((tpa) => (
                        <SelectItem key={tpa.value} value={tpa.value} className="text-xs">
                          {tpa.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTpa && (
                    <p className="text-[10px] text-muted-foreground">
                      {selectedTpa === 'self' 
                        ? 'Your organization handles COBRA administration internally.'
                        : 'Third-party administrator integration coming soon.'}
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Evidence / Attachments</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-6 text-xs"
                    onClick={(e) => { e.stopPropagation(); setShowAddEvidence(true); }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Evidence
                  </Button>
                </div>
                {evidence.length > 0 ? (
                  <div className="space-y-1">
                    {evidence.map((ev) => (
                      <div key={ev.id} className="flex items-center gap-2 text-xs p-1.5 bg-background rounded group">
                        {ev.type === 'file' ? <FileText className="h-3 w-3" /> : <LinkIcon className="h-3 w-3" />}
                        {ev.type === 'link' && ev.linkUrl ? (
                          <a 
                            href={ev.linkUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 truncate text-blue-600 hover:underline"
                          >
                            {ev.linkUrl}
                          </a>
                        ) : (
                          <button
                            onClick={async () => {
                              try {
                                const res = await fetch(`/api/compliance/evidence/${ev.id}/download`, { credentials: 'include' });
                                if (res.ok) {
                                  const { url } = await res.json();
                                  window.open(url, '_blank');
                                }
                              } catch (e) {
                                console.error('Download failed', e);
                              }
                            }}
                            className="flex-1 truncate text-left text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Download className="h-3 w-3 flex-shrink-0" />
                            {ev.fileName || 'Download File'}
                          </button>
                        )}
                        <Badge variant="outline" className="text-[9px]">
                          <Eye className="h-2.5 w-2.5 mr-1" />
                          {ev.visibility === 'employee' ? 'Employee' : 'Internal'}
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                          onClick={() => setEvidenceToDelete(ev.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No evidence attached</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showAddEvidence} onOpenChange={setShowAddEvidence}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Evidence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Evidence Type</Label>
              <Select 
                value={evidenceForm.type} 
                onValueChange={(v) => setEvidenceForm(f => ({ ...f, type: v as 'file' | 'link' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">Link / URL</SelectItem>
                  <SelectItem value="file">File Reference</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {evidenceForm.type === 'link' ? (
              <div className="space-y-2">
                <Label>URL</Label>
                <Input 
                  placeholder="https://example.com/document.pdf"
                  value={evidenceForm.linkUrl}
                  onChange={(e) => setEvidenceForm(f => ({ ...f, linkUrl: e.target.value }))}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Select File</Label>
                <Input 
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="cursor-pointer"
                />
                {selectedFile && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Visible to Employees</Label>
                <p className="text-xs text-muted-foreground">Allow employees to view this evidence</p>
              </div>
              <Switch
                checked={evidenceForm.visibility === 'employee'}
                onCheckedChange={(checked) => setEvidenceForm(f => ({ ...f, visibility: checked ? 'employee' : 'internal' }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEvidence(false)}>Cancel</Button>
            {evidenceForm.type === 'file' ? (
              <Button onClick={handleFileUpload} disabled={uploading || !selectedFile}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload File
              </Button>
            ) : (
              <Button onClick={handleAddEvidence} disabled={addEvidenceMutation.isPending}>
                {addEvidenceMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Add Link
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={evidenceToDelete !== null} onOpenChange={(open) => !open && setEvidenceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Evidence</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this evidence? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (evidenceToDelete) {
                  deleteEvidenceMutation.mutate(evidenceToDelete);
                  setEvidenceToDelete(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function ComplianceChecklist({ clientId, areaKey }: { clientId: number; areaKey: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const areaInfo = AREA_LABELS[areaKey] || { label: areaKey.toUpperCase(), icon: <FileText className="h-4 w-4" /> };

  const { data: itemsData, isLoading, refetch } = useQuery<ComplianceChecklistItem[]>({
    queryKey: ['/api/compliance/checklist-items', clientId, areaKey],
    queryFn: async () => {
      const res = await fetch(`/api/compliance/checklist-items?clientId=${clientId}&areaKey=${areaKey}`, { credentials: 'include' });
      return res.json();
    }
  });
  // Non-array = error/403 body: render nothing and don't auto-seed
  const itemsLoaded = Array.isArray(itemsData);
  const items = asArray<ComplianceChecklistItem>(itemsData);

  const seedMutation = useMutation({
    mutationFn: async () => {
      return apiRequest('POST', '/api/compliance/checklist-items/seed', { clientId, areaKey });
    },
    onSuccess: () => {
      refetch();
      toast({ title: "Default items added", description: `${areaInfo.label} checklist has been populated with default items.` });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      return apiRequest('PATCH', `/api/compliance/checklist-items/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/compliance/checklist-items', clientId, areaKey] });
      queryClient.invalidateQueries({ queryKey: ['/api/compliance/checklist-summary', clientId] });
    }
  });

  const handleStatusChange = (id: number, status: string) => {
    updateMutation.mutate({ id, status });
  };

  const completedCount = items.filter(i => i.status === 'completed').length;
  const progressPercent = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  // Auto-seed default items when none exist
  useEffect(() => {
    if (!isLoading && itemsLoaded && items.length === 0 && !seedMutation.isPending) {
      seedMutation.mutate();
    }
  }, [isLoading, itemsLoaded, items.length]);

  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-200 dark:border-blue-800 rounded-lg cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-950/50 transition-colors">
          <div className="flex items-center gap-2">
            {areaInfo.icon}
            <span className="font-medium text-blue-900 dark:text-blue-100">{areaInfo.label} Compliance Checklist</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">
                {completedCount}/{items.length}
              </span>
            </div>
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pt-3 space-y-3">
          <QuickTemplates areaKey={areaKey} />
          
          {isLoading || (items.length === 0 && seedMutation.isPending) ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading checklist...</span>
            </div>
          ) : !itemsLoaded ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Checklist unavailable.</p>
          ) : items.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Setting up checklist...</span>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <ChecklistItemRow
                  key={item.id}
                  item={item}
                  clientId={clientId}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AuditLogViewer({ clientId }: { clientId: number }) {
  const [expanded, setExpanded] = useState(false);
  
  const { data: logsData } = useQuery<any[]>({
    queryKey: ['/api/compliance/audit-log', clientId],
    queryFn: async () => {
      const res = await fetch(`/api/compliance/audit-log?clientId=${clientId}&limit=20`, { credentials: 'include' });
      return res.json();
    },
    enabled: expanded
  });
  const logs = asArray<any>(logsData);

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" size="sm" className="w-full justify-between">
          <span className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            View Audit Log
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto">
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3 text-center">No audit log entries</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left p-2 font-medium">Time</th>
                  <th className="text-left p-2 font-medium">Action</th>
                  <th className="text-left p-2 font-medium">User</th>
                  <th className="text-left p-2 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-t">
                    <td className="p-2 text-muted-foreground">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="p-2">
                      <Badge variant="outline" className="text-[9px]">
                        {log.action}
                      </Badge>
                    </td>
                    <td className="p-2">{log.actorEmail || 'System'}</td>
                    <td className="p-2 text-muted-foreground truncate max-w-[200px]">
                      {JSON.stringify(log.metadata).slice(0, 50)}...
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
